let audioContext: AudioContext | null = null;
let activeUtterance: SpeechSynthesisUtterance | null = null;
let activeAudio: HTMLAudioElement | null = null;
let activePlaybackSettle: (() => void) | null = null;
let activeSpeechRequest: AbortController | null = null;
let speechGeneration = 0;
let cachedVoices: SpeechSynthesisVoice[] = [];
let voiceListenerInstalled = false;
let neuralSpeechUnavailable = false;

class NeuralSpeechHttpError extends Error {
  public constructor(public readonly status: number) {
    super(`Hebrew TTS request failed (${status}).`);
  }
}

/** Test hook: forget that the neural endpoint reported itself unconfigured. */
export function resetNeuralSpeechAvailability(): void {
  neuralSpeechUnavailable = false;
}

const MAX_NEURAL_SPEECH_CACHE_ENTRIES = 24;

interface CachedSpeechSource {
  readonly source: string;
  readonly objectUrl: boolean;
}

const neuralSpeechCache = new Map<string, CachedSpeechSource>();

export interface SpeechVoiceDescriptor {
  readonly name: string;
  readonly lang: string;
  readonly localService: boolean;
  readonly default: boolean;
}

function normalizedLanguage(language: string): string {
  return language.trim().replaceAll("_", "-").toLowerCase();
}

function voiceScore(voice: SpeechVoiceDescriptor): number {
  const language = normalizedLanguage(voice.lang);
  if (!language.startsWith("he")) return Number.NEGATIVE_INFINITY;
  const name = voice.name.toLowerCase();
  let score = language === "he-il" ? 100 : 80;
  if (/natural|neural/.test(name)) score += 40;
  if (/enhanced|premium/.test(name)) score += 32;
  if (/google|microsoft/.test(name)) score += 20;
  if (/compact|espeak/.test(name)) score -= 60;
  if (voice.default) score += 2;
  if (voice.localService) score += 1;
  return score;
}

export function selectHebrewVoice<T extends SpeechVoiceDescriptor>(voices: readonly T[]): T | undefined {
  return voices
    .map((voice, index) => ({ voice, index, score: voiceScore(voice) }))
    .filter(({ score }) => Number.isFinite(score))
    .sort((left, right) => right.score - left.score || left.index - right.index)[0]?.voice;
}

function availableVoices(synthesis: SpeechSynthesis): SpeechSynthesisVoice[] {
  const refresh = () => {
    const voices = synthesis.getVoices();
    if (voices.length > 0) cachedVoices = voices;
  };
  refresh();
  if (!voiceListenerInstalled) {
    synthesis.addEventListener("voiceschanged", refresh);
    voiceListenerInstalled = true;
  }
  return cachedVoices;
}

function context(): AudioContext | null {
  if (typeof window === "undefined" || !("AudioContext" in window)) return null;
  audioContext ??= new AudioContext();
  if (audioContext.state === "suspended") void audioContext.resume();
  return audioContext;
}

export function playChime(kind: "select" | "correct" | "wrong" | "complete", muted: boolean): void {
  if (muted) return;
  const ctx = context();
  if (!ctx) return;
  const notes = kind === "complete" ? [523, 659, 784, 1047] : kind === "correct" ? [659, 880] : kind === "wrong" ? [330, 294] : [520];
  const now = ctx.currentTime;
  notes.forEach((frequency, index) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = kind === "wrong" ? "sine" : "triangle";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, now + index * 0.11);
    gain.gain.exponentialRampToValueAtTime(kind === "wrong" ? 0.055 : 0.085, now + index * 0.11 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.11 + 0.2);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(now + index * 0.11);
    oscillator.stop(now + index * 0.11 + 0.22);
  });
}

function normalizedSpeechText(text: string): string {
  return text
    .replace(/\s+/gu, " ")
    .replace(/\s+([,.!?])/gu, "$1")
    .trim();
}

function splitLongSpeechChunk(chunk: string): string[] {
  if (chunk.length <= 115) return [chunk];
  const parts = chunk.split(/(?<=,)\s+/u);
  if (parts.length === 1) return [chunk];

  const chunks: string[] = [];
  let current = "";
  for (const part of parts) {
    const candidate = current ? `${current} ${part}` : part;
    if (current && candidate.length > 95) {
      chunks.push(current);
      current = part;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

/**
 * Keeps short Hebrew sentences together while giving introductions, number
 * sequences and the final question their own natural pause.
 */
export function prepareHebrewSpeechChunks(text: string): readonly string[] {
  const normalized = normalizedSpeechText(text);
  if (!normalized) return [];

  const sentences = normalized.match(/[^.!?]+[.!?]?/gu) ?? [normalized];
  const chunks = sentences.flatMap((sentence) => splitLongSpeechChunk(sentence.trim())).filter(Boolean);
  const lastIndex = chunks.length - 1;
  if (lastIndex < 0 || /[.!?]$/u.test(chunks[lastIndex] as string)) return chunks;

  const last = chunks[lastIndex] as string;
  const soundsLikeQuestion = /^(כמה|איזה|איזו|מה|מי|האם|איפה|מתי|איך)(?:\s|$)/u.test(last);
  chunks[lastIndex] = `${last}${soundsLikeQuestion ? "?" : "."}`;
  return chunks;
}

function configuredNeuralSpeechEndpoint(): string | null {
  if (typeof window === "undefined") return null;
  // Must be the literal `import.meta.env.VITE_…` member access: Vite only
  // inlines env vars at exact-match sites, and any aliasing or optional
  // chaining before the key leaves `undefined` in production builds.
  const configured = import.meta.env.VITE_HEBREW_TTS_ENDPOINT?.trim();
  if (!configured) return null;

  try {
    const endpoint = new URL(configured, window.location.href);
    // The provider key must remain behind our own backend, never in Vite or
    // in a direct browser-to-provider request.
    if (endpoint.origin !== window.location.origin) return null;
    return endpoint.href;
  } catch {
    return null;
  }
}

function revokeCachedSource(source: CachedSpeechSource): void {
  if (source.objectUrl && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(source.source);
}

function removeCachedSpeech(cacheKey: string): void {
  const cached = neuralSpeechCache.get(cacheKey);
  if (!cached) return;
  neuralSpeechCache.delete(cacheKey);
  revokeCachedSource(cached);
}

function cacheSpeechSource(cacheKey: string, source: CachedSpeechSource): void {
  const previous = neuralSpeechCache.get(cacheKey);
  if (previous) revokeCachedSource(previous);
  neuralSpeechCache.delete(cacheKey);
  neuralSpeechCache.set(cacheKey, source);

  while (neuralSpeechCache.size > MAX_NEURAL_SPEECH_CACHE_ENTRIES) {
    const oldestKey = neuralSpeechCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    removeCachedSpeech(oldestKey);
  }
}

function safeAudioUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim() || typeof window === "undefined") return null;
  try {
    const url = new URL(value, window.location.href);
    return ["http:", "https:", "blob:", "data:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

async function requestNeuralSpeech(
  endpoint: string,
  text: string,
  signal: AbortSignal,
): Promise<CachedSpeechSource> {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      Accept: "audio/*, application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ text, language: "he-IL" }),
    signal,
  });
  if (!response.ok) throw new NeuralSpeechHttpError(response.status);

  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (contentType.includes("application/json")) {
    const payload = await response.json() as { readonly audioUrl?: unknown };
    const source = safeAudioUrl(payload.audioUrl);
    if (!source) throw new TypeError("Hebrew TTS response did not contain a valid audioUrl.");
    return { source, objectUrl: false };
  }

  const blob = await response.blob();
  if (blob.size === 0 || typeof URL.createObjectURL !== "function") {
    throw new TypeError("Hebrew TTS response did not contain playable audio.");
  }
  return { source: URL.createObjectURL(blob), objectUrl: true };
}

function speakWithBrowserVoice(text: string, generation: number): void {
  if (generation !== speechGeneration) return;
  if (typeof window === "undefined" || !("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) return;

  const chunks = prepareHebrewSpeechChunks(text);
  if (chunks.length === 0) return;
  const synthesis = window.speechSynthesis;
  const voice = selectHebrewVoice(availableVoices(synthesis));
  let index = 0;

  const speakNext = () => {
    if (generation !== speechGeneration || index >= chunks.length) {
      activeUtterance = null;
      return;
    }

    const chunk = chunks[index] as string;
    index += 1;
    const utterance = new SpeechSynthesisUtterance(chunk);
    const isQuestion = chunk.endsWith("?");
    const isNumberSequence = /\d+\s*,/u.test(chunk);
    utterance.lang = "he-IL";
    utterance.rate = isNumberSequence ? 0.84 : 0.88;
    utterance.pitch = isQuestion ? 1.04 : 1;
    utterance.volume = 1;
    if (voice) utterance.voice = voice;
    utterance.onend = () => {
      if (activeUtterance === utterance) speakNext();
    };
    utterance.onerror = () => {
      if (activeUtterance === utterance) activeUtterance = null;
    };
    activeUtterance = utterance;
    synthesis.resume();
    synthesis.speak(utterance);
  };

  speakNext();
}

function fallbackIfCurrent(text: string, generation: number): void {
  if (generation === speechGeneration) speakWithBrowserVoice(text, generation);
}

interface LoadedChunk {
  readonly source: CachedSpeechSource;
  readonly cacheKey: string;
}

async function loadNeuralChunk(
  endpoint: string,
  chunk: string,
  signal: AbortSignal,
): Promise<LoadedChunk> {
  const cacheKey = `${endpoint}\n${chunk}`;
  const cached = neuralSpeechCache.get(cacheKey);
  if (cached) return { source: cached, cacheKey };
  const fetched = await requestNeuralSpeech(endpoint, chunk, signal);
  cacheSpeechSource(cacheKey, fetched);
  return { source: fetched, cacheKey };
}

/** Resolves "ended" on natural completion, "failed" on any playback problem. */
function playNeuralChunk(loaded: LoadedChunk): Promise<"ended" | "failed" | "interrupted"> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !("Audio" in window)) {
      resolve("failed");
      return;
    }
    const audio = new Audio(loaded.source.source);
    audio.preload = "auto";
    let settled = false;
    const settle = (result: "ended" | "failed" | "interrupted") => {
      if (settled) return;
      settled = true;
      if (activePlaybackSettle === settleInterrupted) activePlaybackSettle = null;
      resolve(result);
    };
    const settleInterrupted = () => settle("interrupted");
    audio.onended = () => {
      if (activeAudio === audio) activeAudio = null;
      settle("ended");
    };
    audio.onerror = () => {
      if (activeAudio === audio) activeAudio = null;
      removeCachedSpeech(loaded.cacheKey);
      settle("failed");
    };
    activeAudio = audio;
    activePlaybackSettle = settleInterrupted;
    Promise.resolve(audio.play()).catch(() => {
      if (activeAudio === audio) activeAudio = null;
      removeCachedSpeech(loaded.cacheKey);
      settle("failed");
    });
  });
}

/**
 * Narrates the text one sentence chunk at a time. Fixed sentences ("בואי
 * נחבר.", "מצוין!") repeat across questions, so per-chunk requests hit the
 * server's edge cache and start almost instantly, while only the parts with
 * numbers in them ever cost a fresh synthesis.
 */
async function speakWithNeuralVoice(endpoint: string, text: string, generation: number): Promise<void> {
  const chunks = prepareHebrewSpeechChunks(text);
  if (chunks.length === 0) return;

  const batchController = new AbortController();
  activeSpeechRequest = batchController;
  const pending = chunks.map((chunk) => loadNeuralChunk(endpoint, chunk, batchController.signal));
  for (const request of pending) request.catch(() => undefined);

  for (let index = 0; index < chunks.length; index += 1) {
    let loaded: LoadedChunk;
    try {
      loaded = await (pending[index] as Promise<LoadedChunk>);
    } catch (error) {
      if (activeSpeechRequest === batchController) activeSpeechRequest = null;
      batchController.abort();
      // 503 is the endpoint's "no provider configured" contract: stop paying
      // a failed round-trip on every question until the page reloads.
      // Transient provider errors (502/500) keep retrying on later questions.
      if (error instanceof NeuralSpeechHttpError && error.status === 503) neuralSpeechUnavailable = true;
      if (generation !== speechGeneration || (error instanceof DOMException && error.name === "AbortError")) return;
      fallbackIfCurrent(chunks.slice(index).join(" "), generation);
      return;
    }

    if (generation !== speechGeneration) break;
    const outcome = await playNeuralChunk(loaded);
    if (generation !== speechGeneration || outcome === "interrupted") break;
    if (outcome === "failed") {
      if (activeSpeechRequest === batchController) activeSpeechRequest = null;
      batchController.abort();
      fallbackIfCurrent(chunks.slice(index).join(" "), generation);
      return;
    }
  }
  if (activeSpeechRequest === batchController) activeSpeechRequest = null;
}

export function stopSpeech(): void {
  speechGeneration += 1;
  activeSpeechRequest?.abort();
  activeSpeechRequest = null;

  if (activeAudio) {
    activeAudio.onended = null;
    activeAudio.onerror = null;
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio = null;
  }
  activePlaybackSettle?.();
  activePlaybackSettle = null;

  activeUtterance = null;
  if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
}

export function speakHebrew(text: string, muted: boolean): void {
  stopSpeech();
  if (muted) return;

  const normalized = normalizedSpeechText(text);
  if (!normalized) return;
  const generation = speechGeneration;
  const endpoint = configuredNeuralSpeechEndpoint();
  if (endpoint && !neuralSpeechUnavailable && typeof fetch === "function") {
    void speakWithNeuralVoice(endpoint, normalized, generation);
    return;
  }

  speakWithBrowserVoice(normalized, generation);
}
